import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import axios from 'axios';
import {
  Wrench,
  Trash2,
  Database,
  Download,
  Upload,
  RefreshCw,
  AlertTriangle,
  Settings,
  Calendar,
  BarChart,
  Shield,
  Copy,
  Check,
  X,
  PlayCircle,
  Info,
  Home
} from 'lucide-react';
import PageContainer from '../layout/PageContainer';




interface DataStats {
  transactions: {
    total_count: number;
    first_date: string;
    last_date: string;
    days_with_data: number;
    avg_transaction: number;
    total_sales: number;
  };
  predictions: {
    total_count: number;
    first_date: string;
    last_date: string;
    avg_accuracy: number;
  };
  inventory: {
    total_items: number;
    out_of_stock_items: number;
    low_stock_items: number;
  };
}

interface PredictionConfig {
  dataRange: number;
  minDataPoints: number;
  useOnlyCompleteWeeks: boolean;
  excludeAnomalies: boolean;
  excludedDates: string[];
  predictionMode: 'development' | 'production' | 'demo';
}

const DeveloperTools: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'data' | 'generator' | 'config' | 'export' | 'debug'>('data');
  const [dataStats, setDataStats] = useState<DataStats | null>(null);
  const [predictionConfig, setPredictionConfig] = useState<PredictionConfig>({
    dataRange: 30,
    minDataPoints: 7,
    useOnlyCompleteWeeks: false,
    excludeAnomalies: true,
    excludedDates: [],
    predictionMode: 'development'
  });
  
  // データクリア用の状態
  const [clearDateRange, setClearDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [clearDataTypes, setClearDataTypes] = useState({
    transactions: true,
    inventory_logs: false,
    ai_predictions: true,
    prediction_accuracy: true
  });
  const [confirmCode, setConfirmCode] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // ダミーデータ生成用の状態
  const [generatorConfig, setGeneratorConfig] = useState({
    days: 30,
    pattern: 'realistic',
    startDate: new Date().toISOString().split('T')[0],
    baseCustomers: 45,
    variance: 0.2
  });
  
  // 通知関連
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // デバッグ情報
  const [recentTransactions, setRecentTransactions] = useState<any>(null);

  useEffect(() => {
    fetchDataStats();
    fetchPredictionConfig();
  }, []);

  const fetchDataStats = async () => {
    try {
      const response = await apiClient.get('/developer-tools/data-stats');
      setDataStats(response.data);
    } catch (error) {
      console.error('データ統計取得エラー:', error);
    }
  };

  const fetchPredictionConfig = async () => {
    try {
      const response = await apiClient.get('/developer-tools/prediction-config');
      setPredictionConfig(response.data);
    } catch (error) {
      console.error('予測設定取得エラー:', error);
    }
  };

  const fetchRecentTransactions = async () => {
    try {
      const response = await apiClient.get('/developer-tools/recent-transactions');
      setRecentTransactions(response.data);
    } catch (error) {
      console.error('最近のトランザクション取得エラー:', error);
    }
  };

  const handleClearData = async () => {
    if (confirmCode !== 'DELETE-CONFIRM') {
      showNotification('error', '確認コードが正しくありません');
      return;
    }

    setIsProcessing(true);
    try {
      const dataTypes = Object.keys(clearDataTypes).filter(key => clearDataTypes[key as keyof typeof clearDataTypes]);
      
      const response = await apiClient.post('/developer-tools/clear-data', {
        startDate: clearDateRange.start,
        endDate: clearDateRange.end,
        dataTypes,
        confirmCode
      });

      showNotification('success', `データを削除しました。バックアップ: ${response.data.backupPath}`);
      setShowConfirmDialog(false);
      setConfirmCode('');
      fetchDataStats();
    } catch (error: any) {
      showNotification('error', error.response?.data?.error || 'データ削除に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGenerateDummyData = async () => {
    setIsProcessing(true);
    try {
      const response = await apiClient.post('/developer-tools/generate-dummy-data', generatorConfig);
      showNotification('success', response.data.message);
      fetchDataStats();
    } catch (error: any) {
      showNotification('error', error.response?.data?.error || 'ダミーデータ生成に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdatePredictionConfig = async () => {
    setIsProcessing(true);
    try {
      await apiClient.post('/developer-tools/prediction-config', predictionConfig);
      showNotification('success', '予測設定を更新しました');
    } catch (error: any) {
      showNotification('error', error.response?.data?.error || '設定更新に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportData = async () => {
    try {
      const params = new URLSearchParams({
        startDate: clearDateRange.start,
        endDate: clearDateRange.end,
        dataTypes: 'transactions,inventory,predictions'
      });
      
      const response = await apiClient.get('/developer-tools/export-data?${params}', {
        responseType: 'blob'
      });
      
      // ダウンロード処理
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `waton_export_${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showNotification('success', 'データをエクスポートしました');
    } catch (error) {
      showNotification('error', 'エクスポートに失敗しました');
    }
  };

  const showNotification = (type: 'success' | 'error' | 'warning', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const formatNumber = (num: number) => {
    return num?.toLocaleString() || '0';
  };

  // 開発環境チェック
  const isDevelopment = process.env.NODE_ENV !== 'production';

  if (!isDevelopment && !window.location.search.includes('dev=true')) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">アクセス拒否</h2>
          <p className="text-gray-600">開発者ツールは開発環境でのみ使用できます</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* 通知 */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' :
          notification.type === 'error' ? 'bg-red-500' :
          'bg-yellow-500'
        } text-white flex items-center gap-2`}>
          {notification.type === 'success' && <Check className="h-5 w-5" />}
          {notification.type === 'error' && <X className="h-5 w-5" />}
          {notification.type === 'warning' && <AlertTriangle className="h-5 w-5" />}
          {notification.message}
        </div>
      )}

      {/* ヘッダー */}
      <div className="mb-6 bg-gray-900 text-white p-4 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wrench className="h-8 w-8 text-yellow-400" />
            <div>
              <h1 className="text-2xl font-bold">開発者ツール</h1>
              <p className="text-gray-300 text-sm">データ管理と予測設定</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Home className="w-5 h-5" />
              ホーム
            </button>
            <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-semibold">
              開発モード
            </div>
          </div>
        </div>
      </div>

      {/* データ統計 */}
      {dataStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-700">売上データ</h3>
              <Database className="h-5 w-5 text-blue-500" />
            </div>
            <div className="space-y-1 text-sm">
              <p>件数: <span className="font-bold">{formatNumber(dataStats?.transactions?.total_count || 0)}</span></p>
              <p>期間: {dataStats?.transactions?.first_date || 'なし'} ～ {dataStats?.transactions?.last_date || 'なし'}</p>
              <p>日数: {dataStats?.transactions?.days_with_data || 0}日</p>
              <p>総売上: ¥{formatNumber(dataStats?.transactions?.total_sales || 0)}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-700">AI予測</h3>
              <BarChart className="h-5 w-5 text-purple-500" />
            </div>
            <div className="space-y-1 text-sm">
              <p>予測数: <span className="font-bold">{formatNumber(dataStats?.predictions?.total_count || 0)}</span></p>
              <p>期間: {dataStats?.predictions?.first_date || 'なし'} ～ {dataStats?.predictions?.last_date || 'なし'}</p>
              <p>平均精度: {dataStats?.predictions?.avg_accuracy?.toFixed(1) || 0}%</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-700">在庫状況</h3>
              <Info className="h-5 w-5 text-green-500" />
            </div>
            <div className="space-y-1 text-sm">
              <p>品目数: <span className="font-bold">{formatNumber(dataStats?.inventory?.total_items || 0)}</span></p>
              <p>在庫切れ: <span className="text-red-600 font-bold">{dataStats?.inventory?.out_of_stock_items || 0}</span></p>
              <p>要発注: <span className="text-yellow-600 font-bold">{dataStats?.inventory?.low_stock_items || 0}</span></p>
            </div>
          </div>
        </div>
      )}

      {/* タブナビゲーション */}
      <div className="mb-6 border-b">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('data')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'data'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Trash2 className="h-4 w-4" />
            データクリア
          </button>
          <button
            onClick={() => setActiveTab('generator')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'generator'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <PlayCircle className="h-4 w-4" />
            ダミーデータ生成
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'config'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Settings className="h-4 w-4" />
            予測設定
          </button>
          <button
            onClick={() => setActiveTab('export')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'export'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Download className="h-4 w-4" />
            インポート/エクスポート
          </button>
          <button
            onClick={() => {
              setActiveTab('debug');
              fetchRecentTransactions();
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'debug'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Info className="h-4 w-4" />
            デバッグ
          </button>
        </nav>
      </div>

      {/* データクリアタブ */}
      {activeTab === 'data' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              テストデータクリア
            </h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-700 text-sm">
                ⚠️ この操作は取り消せません。削除前に自動的にバックアップが作成されます。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
                <input
                  type="date"
                  value={clearDateRange.start}
                  onChange={(e) => setClearDateRange({ ...clearDateRange, start: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">終了日</label>
                <input
                  type="date"
                  value={clearDateRange.end}
                  onChange={(e) => setClearDateRange({ ...clearDateRange, end: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">削除対象データ</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={clearDataTypes.transactions}
                    onChange={(e) => setClearDataTypes({ ...clearDataTypes, transactions: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">売上トランザクション</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={clearDataTypes.inventory_logs}
                    onChange={(e) => setClearDataTypes({ ...clearDataTypes, inventory_logs: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">在庫ログ</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={clearDataTypes.ai_predictions}
                    onChange={(e) => setClearDataTypes({ ...clearDataTypes, ai_predictions: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">AI予測データ</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={clearDataTypes.prediction_accuracy}
                    onChange={(e) => setClearDataTypes({ ...clearDataTypes, prediction_accuracy: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">予測精度データ</span>
                </label>
              </div>
            </div>

            <button
              onClick={() => setShowConfirmDialog(true)}
              disabled={isProcessing}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              データを削除
            </button>
          </div>

          {/* 確認ダイアログ */}
          {showConfirmDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl max-w-md">
                <h3 className="text-lg font-bold mb-4">データ削除の確認</h3>
                <p className="mb-4 text-sm">
                  本当にデータを削除しますか？確認コード「DELETE-CONFIRM」を入力してください。
                </p>
                <input
                  type="text"
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value)}
                  placeholder="DELETE-CONFIRM"
                  className="w-full px-3 py-2 border rounded-md mb-4"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowConfirmDialog(false);
                      setConfirmCode('');
                    }}
                    className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                  >
                    キャンセル
                  </button>
                  <button
                    onClick={handleClearData}
                    disabled={confirmCode !== 'DELETE-CONFIRM' || isProcessing}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    削除実行
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ダミーデータ生成タブ */}
      {activeTab === 'generator' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-500" />
            ダミーデータ生成
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">生成日数</label>
              <input
                type="number"
                value={generatorConfig.days}
                onChange={(e) => setGeneratorConfig({ ...generatorConfig, days: parseInt(e.target.value) })}
                min="1"
                max="365"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
              <input
                type="date"
                value={generatorConfig.startDate}
                onChange={(e) => setGeneratorConfig({ ...generatorConfig, startDate: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">パターン</label>
              <select
                value={generatorConfig.pattern}
                onChange={(e) => setGeneratorConfig({ ...generatorConfig, pattern: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="realistic">リアルパターン（曜日考慮）</option>
                <option value="weekday">平日のみ</option>
                <option value="weekend">週末のみ</option>
                <option value="random">ランダム</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">基準来客数</label>
              <input
                type="number"
                value={generatorConfig.baseCustomers}
                onChange={(e) => setGeneratorConfig({ ...generatorConfig, baseCustomers: parseInt(e.target.value) })}
                min="10"
                max="200"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">変動率</label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  value={generatorConfig.variance}
                  onChange={(e) => setGeneratorConfig({ ...generatorConfig, variance: parseFloat(e.target.value) })}
                  min="0"
                  max="0.5"
                  step="0.05"
                  className="flex-1"
                />
                <span className="text-sm font-medium w-12">{(generatorConfig.variance * 100).toFixed(0)}%</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-blue-700 text-sm">
              💡 「リアルパターン」では曜日ごとの実際の営業傾向を再現します。
              週末は平日の約1.3倍の来客数になります。
            </p>
          </div>

          <button
            onClick={handleGenerateDummyData}
            disabled={isProcessing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                生成中...
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4" />
                ダミーデータを生成
              </>
            )}
          </button>
        </div>
      )}

      {/* 予測設定タブ */}
      {activeTab === 'config' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Settings className="h-5 w-5 text-purple-500" />
            AI予測設定
          </h2>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                予測モード
              </label>
              <select
                value={predictionConfig.predictionMode}
                onChange={(e) => setPredictionConfig({ 
                  ...predictionConfig, 
                  predictionMode: e.target.value as 'development' | 'production' | 'demo' 
                })}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="development">開発モード（テストデータ使用）</option>
                <option value="production">本番モード（実データ使用）</option>
                <option value="demo">デモモード（固定データ使用）</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                予測に使用するデータ期間（日数）
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  value={predictionConfig.dataRange}
                  onChange={(e) => setPredictionConfig({ 
                    ...predictionConfig, 
                    dataRange: parseInt(e.target.value) 
                  })}
                  min="7"
                  max="365"
                  className="flex-1"
                />
                <span className="text-sm font-medium w-16">{predictionConfig.dataRange}日</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                最小必要データ数
              </label>
              <input
                type="number"
                value={predictionConfig.minDataPoints}
                onChange={(e) => setPredictionConfig({ 
                  ...predictionConfig, 
                  minDataPoints: parseInt(e.target.value) 
                })}
                min="1"
                max="30"
                className="w-32 px-3 py-2 border rounded-md"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={predictionConfig.useOnlyCompleteWeeks}
                  onChange={(e) => setPredictionConfig({ 
                    ...predictionConfig, 
                    useOnlyCompleteWeeks: e.target.checked 
                  })}
                  className="mr-2"
                />
                <span className="text-sm">完全な週のデータのみ使用</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={predictionConfig.excludeAnomalies}
                  onChange={(e) => setPredictionConfig({ 
                    ...predictionConfig, 
                    excludeAnomalies: e.target.checked 
                  })}
                  className="mr-2"
                />
                <span className="text-sm">異常値を自動除外</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                除外する日付
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  className="px-3 py-2 border rounded-md"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const date = (e.target as HTMLInputElement).value;
                      if (date && !predictionConfig.excludedDates.includes(date)) {
                        setPredictionConfig({
                          ...predictionConfig,
                          excludedDates: [...predictionConfig.excludedDates, date]
                        });
                        (e.target as HTMLInputElement).value = '';
                      }
                    }
                  }}
                />
                <button className="px-3 py-2 bg-gray-200 rounded-md text-sm">
                  Enterで追加
                </button>
              </div>
              {predictionConfig.excludedDates.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {predictionConfig.excludedDates.map((date) => (
                    <span
                      key={date}
                      className="px-2 py-1 bg-gray-100 rounded-md text-sm flex items-center gap-1"
                    >
                      {date}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-red-500"
                        onClick={() => setPredictionConfig({
                          ...predictionConfig,
                          excludedDates: predictionConfig.excludedDates.filter(d => d !== date)
                        })}
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleUpdatePredictionConfig}
            disabled={isProcessing}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            設定を保存
          </button>
        </div>
      )}

      {/* インポート/エクスポートタブ */}
      {activeTab === 'export' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Download className="h-5 w-5 text-green-500" />
            データのインポート/エクスポート
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium mb-3">エクスポート</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  指定期間のデータをJSON形式でエクスポートします
                </p>
                <button
                  onClick={handleExportData}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  データをエクスポート
                </button>
              </div>
            </div>

            <div>
              <h3 className="font-medium mb-3">インポート</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  JSONファイルからデータをインポートします
                </p>
                <input
                  type="file"
                  accept=".json"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = async (event) => {
                        try {
                          const data = JSON.parse(event.target?.result as string);
                          const response = await apiClient.post('/developer-tools/import-data', {
                            data,
                            overwrite: false
                          });
                          showNotification('success', response.data.message);
                          fetchDataStats();
                        } catch (error) {
                          showNotification('error', 'インポートに失敗しました');
                        }
                      };
                      reader.readAsText(file);
                    }
                  }}
                  className="hidden"
                  id="import-file"
                />
                <label
                  htmlFor="import-file"
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Upload className="h-4 w-4" />
                  ファイルを選択
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* デバッグタブ */}
      {activeTab === 'debug' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-500" />
            デバッグ情報
          </h2>

          <div className="space-y-4">
            {/* 最近のトランザクション */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">最近のトランザクション</h3>
                <button
                  onClick={fetchRecentTransactions}
                  className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                >
                  更新
                </button>
              </div>
              
              {recentTransactions ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">
                    データ件数: {recentTransactions.count}件
                  </p>
                  {recentTransactions.message && (
                    <p className={`text-sm mb-3 ${recentTransactions.count === 0 ? 'text-red-600 font-medium' : 'text-green-600'}`}>
                      {recentTransactions.message}
                    </p>
                  )}
                  
                  {recentTransactions.count === 0 ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-yellow-700">
                        データベースにトランザクションデータがありません。
                        「ダミーデータ生成」タブから、テストデータを生成してください。
                      </p>
                      <button
                        onClick={() => setActiveTab('generator')}
                        className="mt-2 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                      >
                        ダミーデータ生成へ
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left">ID</th>
                            <th className="px-3 py-2 text-left">日時</th>
                            <th className="px-3 py-2 text-left">種類</th>
                            <th className="px-3 py-2 text-left">支払</th>
                            <th className="px-3 py-2 text-left">金額</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {recentTransactions.transactions?.map((tx: any) => (
                            <tr key={tx.id}>
                              <td className="px-3 py-2">{tx.id}</td>
                              <td className="px-3 py-2">{tx.local_time || tx.timestamp}</td>
                              <td className="px-3 py-2">{tx.order_type}</td>
                              <td className="px-3 py-2">{tx.payment_type}</td>
                              <td className="px-3 py-2">¥{tx.total?.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">データを取得していません</p>
              )}
            </div>

            {/* AI予測の状態 */}
            <div>
              <h3 className="font-medium mb-2">AI予測の状態</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-sm">
                  <span className="font-medium">Gemini APIキー:</span>{' '}
                  <span className={process.env.GEMINI_API_KEY && process.env.GEMINI_API_KEY !== 'your_gemini_key_here' ? 'text-green-600' : 'text-red-600'}>
                    {process.env.GEMINI_API_KEY && process.env.GEMINI_API_KEY !== 'your_gemini_key_here' ? '設定済み' : '未設定（簡易予測モード）'}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="font-medium">予測モード:</span>{' '}
                  {predictionConfig.predictionMode}
                </p>
                <p className="text-sm">
                  <span className="font-medium">データ範囲:</span>{' '}
                  過去{predictionConfig.dataRange}日間
                </p>
                {recentTransactions?.count === 0 && (
                  <p className="text-sm text-red-600 font-medium">
                    ⚠️ トランザクションデータがないため、デフォルト値を使用しています
                  </p>
                )}
              </div>
            </div>
            
            {/* 予測テスト */}
            <div className="mt-4">
              <h3 className="font-medium mb-2">予測テスト</h3>
              <button
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API_BASE_URL}/developer-tools/test-prediction`);
                    console.log('予測テスト結果:', response.data);
                    alert(`予測テスト完了
                    
デバッグ情報:
- トランザクション総数: ${response.data.debug_info.total_transactions}
- 同曜日データ: ${response.data.debug_info.weekday_data_count}件
- 予測客数: ${response.data.prediction.predicted_customers}人
- 予測売上: ¥${response.data.prediction.predicted_sales.toLocaleString()}
- 予測方法: ${response.data.prediction.method}

詳細はコンソールを確認してください。`);
                  } catch (error) {
                    console.error('予測テストエラー:', error);
                    alert('予測テストに失敗しました。コンソールを確認してください。');
                  }
                }}
                className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
              >
                予測動作をテスト
              </button>
              <p className="text-xs text-gray-500 mt-1">
                現在のデータベースを使用して予測が正しく動作するか確認します
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperTools;